import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ShieldCheck, ChevronRight, ChevronLeft } from 'lucide-react';
import './FormPage.css';

const LOCATIONS = [
  'Head Office - 1st Floor', 'Head Office - 2nd Floor', 'Truck Park/Waiting Area',
  'Raw Material Warehouse', 'Powerhouse/Generator Area', 'Kitchen/Canteen', 'Laundry',
  'Quality Laboratory', 'Seasoning Room/Meat Room', 'Rondo 1 - 2', 'Rondo 3 - 4', 'Rondo 5',
  'Oven', 'Snacks Wrapping Section', 'Coconut Production/Wrapping', 'Pastry Dough Mixing',
  'Pastry Mixing Room', 'Pastry Kitchen', 'Bread Mixing Room', 'Engineering Workshop',
  'Engineering Store', 'Loading Bay', 'Waste Area/ETP', 'Dairies Production Office',
  'Ambient Warehouse', 'Compressor Workshop', 'Pouch/Waltzer Section', 'Dairies Mixing Room',
  'Bowl Washing/Bulk Section', 'Dairies Engineering Office/Gas Generator House', 'Dairies Boiler House',
  'SWAN Filling Room', 'Generator Area/Gate House', 'SWAN Production Dry End', 'Entire Factory',
  'SWAN Quality Laboratory/Entrance Control', 'Preform Tipping/Staging Area', 'Pet Crush Site',
  'SWAN Contanerized Storage', 'Clinic', 'Pallet Repair Section'
];

const CATEGORIES = [
  'PPE Usage & Compliance', 'PPE Condition & Maintenance', 'Specialized PPE (food-grade / cold-store)',
  'Manual Handling & Lifting Technique', 'Repetitive Motion & Posture', 'Work Positioning & Reach',
  'Lockout/Tagout (LOTO) Compliance', 'Machine Guarding & Interlocks', 'Equipment Inspection & Pre-use Checks',
  'Conveyor & Automated Line Interaction', 'Work Area Cleanliness & Organisation', 'Spill Management & Clean-up',
  'Aisle & Walkway Clearance', 'Waste Segregation & Disposal', 'Cleaning Agent & Sanitiser Handling',
  'Food-grade Chemical Storage & Labelling', 'CO₂/Refrigerant Gas Safety', 'Ladder & Platform Use',
  'Fall Prevention & Arrest Systems', 'Overhead Work & Falling Object Controls', 'Electrical Panel & Switchgear Access',
  'Temporary Wiring & Extension Leads', 'Forklift/MHE Awareness & Right-of-Way', 'Pedestrian Lane Discipline',
  'Loading Dock & Yard Safety', 'Hand Hygiene & Gowning Compliance', 'Foreign Body Control (FBC) Adherence',
  'Allergen Segregation Practice', 'Cold Chain & Temperature Discipline', 'PTW Compliance & Authorisation',
  'Standard Operating Procedure (SOP) Adherence', 'Hot Work & Confined Space Controls', 'Compressed Air Usage',
  'Steam & Hot Water Systems', 'Refrigeration/Ammonia Plant Behaviour', 'Fire Exit & Emergency Route Behaviour',
  'Fire Extinguisher Access & Usage', 'Emergency Response Readiness', 'Safety Communication & Reporting Culture',
  'Coaching & Intervention Behaviour', 'Fatigue & Alertness Management', 'Compliance with Safety Instructions'
];

const ANTECEDENTS = [
  'Inadequate or absent safety training', 'New/transferred employee unfamiliar with task', 'Misunderstanding of SOP or work instruction',
  'Low safety literacy or language barrier', 'Overconfidence/complacency from routine', 'Risk under-estimation ("it won\'t happen to me")',
  'Peer pressure/social normalisation of risk', 'Desire to demonstrate capability or speed', 'Low personal risk perception for the task',
  'Production target/schedule pressure', 'Understaffing or absent colleague cover', 'Short-cut taken to save time',
  'End-of-shift fatigue or rushing', 'Poor housekeeping or cluttered workspace', 'Inadequate lighting or signage',
  'Equipment design promotes unsafe behaviour', 'Extreme temperature (cold store/oven area)', 'Noise reducing verbal communication',
  'PPE unavailable, wrong size, or uncomfortable', 'Defective or unserviceable equipment used', 'Correct tool not available – improvisation used',
  'Inadequate safety guards or controls', 'No procedure exists for the task', 'Outdated or unclear SOP',
  'Procedure impractical – routinely bypassed', 'PTW/LOTO system not enforced', 'Absent or insufficient supervision',
  'Previous unsafe behaviour went unaddressed', 'Supervisor modelled or condoned unsafe act', 'Low visible felt leadership on the floor',
  'Safety feedback culture is weak or punitive', 'Production over safety trade-off normalised', 'Low team morale or engagement',
  'Safety incentives create under-reporting bias', 'High staff turnover reducing safety knowledge', 'Contractor/visitor unfamiliar with site rules',
  'Fatigue (shift duration, poor rest)', 'Physical incapacity or health condition', 'Distraction (phone, personal concern)',
  'Rushing due to personal urgency', 'Effective safety training & competency building', 'Strong visible felt leadership by management',
  'Positive peer safety culture & coaching', 'Clear, accessible, and practical procedures', 'Adequate supervision and feedback loop',
  'Good availability and fit of PPE', 'Intrinsic safety motivation & ownership'
];

const steps = [
  { id: 1, label: 'Observer Info' },
  { id: 2, label: 'Observation Context' },
  { id: 3, label: 'Behavior Classification' },
  { id: 4, label: 'Narrative & Status' },
];

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => date.toISOString().split('T')[0];

// Calculate min and max dates for observation date
const getTodayDate = () => {
  const today = new Date();
  return formatDate(today);
};

const getMinDate = () => {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - 2); // 2 days ago
  return formatDate(minDate);
};

const isValidDateInput = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
};

const isObservationDateAllowed = (value) => {
  if (!isValidDateInput(value)) return false;
  const date = new Date(value);
  const today = new Date(getTodayDate());
  const minDate = new Date(getMinDate());
  return date >= minDate && date <= today;
};

const emptyForm = {
  observationDate: '', shift: '', observationLocation: '',
  behaviorType: '', behaviorCategory: '', antecedent: '',
  behaviorDescription: '', otherBehaviorsObserved: ''
};

export default function FormPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [observer, setObserver] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formFields, setFormFields] = useState({
    shift: ['Day', 'Night'],
    location: LOCATIONS,
    category: CATEGORIES,
    antecedent: ANTECEDENTS
  });

  useEffect(() => {
    Promise.all([
      axios.get(`/api/form/${slug}`),
      axios.get('/api/form-fields')
    ])
      .then(([obsRes, fieldsRes]) => {
        setObserver(obsRes.data);
        if (fieldsRes.data && Object.keys(fieldsRes.data).length > 0) {
          setFormFields(prev => ({
            shift: fieldsRes.data.shift || prev.shift,
            location: fieldsRes.data.location || prev.location,
            category: fieldsRes.data.category || prev.category,
            antecedent: fieldsRes.data.antecedent || prev.antecedent
          }));
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setErrors(ev => ({ ...ev, [k]: '' }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 2) {
      if (!form.observationDate) {
        e.observationDate = 'Required';
      } else if (!isValidDateInput(form.observationDate)) {
        e.observationDate = 'Invalid date';
      } else if (!isObservationDateAllowed(form.observationDate)) {
        e.observationDate = `Observation date must be between ${getMinDate()} and ${getTodayDate()}`;
      }
      if (!form.shift) e.shift = 'Required';
      if (!form.observationLocation) e.observationLocation = 'Required';
    }
    if (s === 3) {
      if (!form.behaviorType) e.behaviorType = 'Required';
      if (!form.behaviorCategory) e.behaviorCategory = 'Required';
      if (!form.antecedent) e.antecedent = 'Required';
    }
    if (s === 4) {
      if (!form.behaviorDescription.trim()) e.behaviorDescription = 'Required';
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setStep(s => s + 1);
  };

  const back = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    const e = {
      ...validateStep(2),
      ...validateStep(3),
      ...validateStep(4)
    };
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/form/${slug}`, form);
      navigate(`/submit/${slug}/success`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return (
    <div className="form-shell">
      <div className="form-not-found">
        <ShieldCheck size={32} strokeWidth={1.5} />
        <h2>Invalid Form Link</h2>
        <p>This form link is invalid or has been deactivated. Please contact your HSE administrator.</p>
      </div>
    </div>
  );

  if (!observer) return (
    <div className="form-shell">
      <div className="form-loading">
        <div className="form-spinner" />
        <span>Loading your form…</span>
      </div>
    </div>
  );

  return (
    <div className="form-shell">
      <div className="form-container fade-in">
        {/* Header */}
        <div className="form-header">
          <div className="form-logo">
            <ShieldCheck size={16} strokeWidth={2} />
            <span>UFL HSE</span>
          </div>
          <h1 className="form-main-title">BBS & VFL Observation Register</h1>
          <p className="form-org">Organisation: FMCG Manufacturing &nbsp;·&nbsp; Business Units: SNACKS | DAIRIES | SWAN</p>
        </div>

        {/* Stepper */}
        <div className="form-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`step ${step === s.id ? 'step--active' : ''} ${step > s.id ? 'step--done' : ''}`}>
                <div className="step-dot">{step > s.id ? '✓' : s.id}</div>
                <span className="step-label">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`step-line ${step > s.id ? 'step-line--done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="form-body">

          {/* Step 1: Observer Info (read-only) */}
          {step === 1 && (
            <div className="form-section fade-in">
              <div className="form-section-title">Section 1 — Observer Information</div>
              <p className="form-section-sub">Your information is pre-filled and linked to this form. Please verify it is correct.</p>
              <div className="readonly-grid">
                <div className="readonly-field"><span className="readonly-label">Full Name</span><span className="readonly-value">{observer.fullName}</span></div>
                <div className="readonly-field"><span className="readonly-label">Email Address</span><span className="readonly-value">{observer.email}</span></div>
                <div className="readonly-field"><span className="readonly-label">Business Unit</span><span className="readonly-value">{observer.businessUnit}</span></div>
                <div className="readonly-field"><span className="readonly-label">Department</span><span className="readonly-value">{observer.department}</span></div>
                <div className="readonly-field"><span className="readonly-label">Designation</span><span className="readonly-value">{observer.designation}</span></div>
              </div>
            </div>
          )}

          {/* Step 2: Observation Context */}
          {step === 2 && (
            <div className="form-section fade-in">
              <div className="form-section-title">Section 2 — Observation Context</div>
              <div className="form-grid">
                <div className={`form-field ${errors.observationDate ? 'form-field--error' : ''}`}>
                  <label className="form-label">Observation Date</label>
                  <input type="date" className="form-input" value={form.observationDate} onChange={set('observationDate')} min={getMinDate()} max={getTodayDate()} />
                  {errors.observationDate && <span className="form-error">{errors.observationDate}</span>}
                </div>
                <div className={`form-field ${errors.shift ? 'form-field--error' : ''}`}>
                  <label className="form-label">Shift</label>
                  <select className="form-input form-select" value={form.shift} onChange={set('shift')}>
                    <option value="">Select shift</option>
                    {formFields.shift.map(s => <option key={s}>{s}</option>)}
                  </select>
                  {errors.shift && <span className="form-error">{errors.shift}</span>}
                </div>
                <div className={`form-field form-field--full ${errors.observationLocation ? 'form-field--error' : ''}`}>
                  <label className="form-label">Observation Location</label>
                  <select className="form-input form-select" value={form.observationLocation} onChange={set('observationLocation')}>
                    <option value="">Select location</option>
                    {formFields.location.map(l => <option key={l}>{l}</option>)}
                  </select>
                  {errors.observationLocation && <span className="form-error">{errors.observationLocation}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Behavior Classification */}
          {step === 3 && (
            <div className="form-section fade-in">
              <div className="form-section-title">Section 3 — Behavior Classification</div>
              <div className="form-grid">
                <div className={`form-field ${errors.behaviorType ? 'form-field--error' : ''}`}>
                  <label className="form-label">Behavior Type</label>
                  <select className="form-input form-select" value={form.behaviorType} onChange={set('behaviorType')}>
                    <option value="">Select behavior type</option>
                    <option>Safe Behavior</option>
                    <option>At-Risk Behavior</option>
                  </select>
                  {errors.behaviorType && <span className="form-error">{errors.behaviorType}</span>}
                </div>
                <div className={`form-field ${errors.behaviorCategory ? 'form-field--error' : ''}`}>
                  <label className="form-label">Behavior Category</label>
                  <select className="form-input form-select" value={form.behaviorCategory} onChange={set('behaviorCategory')}>
                    <option value="">Select category</option>
                    {formFields.category.map(c => <option key={c}>{c}</option>)}
                  </select>
                  {errors.behaviorCategory && <span className="form-error">{errors.behaviorCategory}</span>}
                </div>
                <div className={`form-field form-field--full ${errors.antecedent ? 'form-field--error' : ''}`}>
                  <label className="form-label">Antecedent / Possible Reason</label>
                  <select className="form-input form-select" value={form.antecedent} onChange={set('antecedent')}>
                    <option value="">Select antecedent</option>
                    {formFields.antecedent.map(a => <option key={a}>{a}</option>)}
                  </select>
                  {errors.antecedent && <span className="form-error">{errors.antecedent}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Narrative & Status */}
          {step === 4 && (
            <div className="form-section fade-in">
              <div className="form-section-title">Section 4 — Narrative & Status</div>
              <div className="form-grid">
                <div className={`form-field form-field--full ${errors.behaviorDescription ? 'form-field--error' : ''}`}>
                  <label className="form-label">Behavior Description <span className="form-required">*</span></label>
                  <textarea
                    className="form-input form-textarea"
                    rows={4}
                    placeholder="Describe the observed behavior in detail…"
                    value={form.behaviorDescription}
                    onChange={set('behaviorDescription')}
                  />
                  {errors.behaviorDescription && <span className="form-error">{errors.behaviorDescription}</span>}
                </div>
                <div className="form-field form-field--full">
                  <label className="form-label">Other Behaviors Observed <span className="form-optional">(optional)</span></label>
                  <textarea
                    className="form-input form-textarea"
                    rows={3}
                    placeholder="Any additional behaviors noted during observation…"
                    value={form.otherBehaviorsObserved}
                    onChange={set('otherBehaviorsObserved')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="form-nav">
          <div>
            {step > 1 && (
              <button className="form-btn form-btn--back" onClick={back}>
                <ChevronLeft size={14} /> Back
              </button>
            )}
          </div>
          <div className="form-step-count">Step {step} of {steps.length}</div>
          <div>
            {step < 4 ? (
              <button className="form-btn form-btn--next" onClick={next}>
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button className="form-btn form-btn--submit" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <span className="form-spinner form-spinner--sm" /> : null}
                {submitting ? 'Submitting…' : 'Submit Observation'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
